import { useFormik } from 'formik';
import { Link, useNavigate, useSearch } from 'react-location';
import * as Yup from 'yup';
import { useLocalStorage } from 'react-use';
import { setAuth } from 'state/auth';
import { LocationGenerics } from 'router/location';
import { FC, useState } from 'react';
import useApi from 'utils/use-api';
import toast from 'react-hot-toast';

const ForgotPasswordPage: FC = () => {
  const navigate = useNavigate();
  const search = useSearch<LocationGenerics>();
  const [forgotStore, setForgotStore] = useLocalStorage<{ email?: string; tab: string; token?: string }>('forgot-store', {
    email: '',
    tab: 'send-code',
    token: '',
  });
  const api = useApi();
  const [loadingSend, setLoadingSend] = useState(false);
  const [loadingVerify, setLoadingVerify] = useState(false);
  const [loadingReset, setLoadingReset] = useState(false);

  const sendCodeForm = useFormik({
    initialValues: {
      email: '',
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .email('Invalid email address')
        .required('Email address is required')
        .test('allowed-domain', 'Email must be from @wewire.com or @ogateway.co', value => {
          if (!value) return false;
          return value.toLowerCase().endsWith('@wewire.com') || value.toLowerCase().endsWith('@ogateway.co');
        }),
    }),
    onSubmit: async values => {
      setLoadingSend(true);
      try {
        await api.post('/auth/password/send-code', {
          email: values.email || forgotStore?.email,
        });
        setForgotStore({ tab: 'verify-code', email: values.email || forgotStore?.email });
        toast.success(JSON.stringify({ type: 'success', title: 'Verification code sent' }));
      } catch (err: any) {
        toast.error(JSON.stringify({ type: 'error', title: err?.response?.data?.message ?? 'Unable to reset password' }));
      } finally {
        setLoadingSend(false);
      }
    },
  });

  const verifyCodeForm = useFormik({
    initialValues: {
      code: '',
    },
    validationSchema: Yup.object({
      code: Yup.string().min(6, 'Code should be 6 digits').required('Enter reset code'),
    }),
    onSubmit: async values => {
      const payload = {
        email: forgotStore?.email,
        code: values.code,
      };
      setLoadingVerify(true);
      try {
        const { data } = await api.post('/auth/password/verify-code', payload);
        setForgotStore({
          tab: 'reset-password',
          token: data?.token ?? data?.verifyUserCode?.token,
          email: data?.user?.email ?? data?.verifyUserCode?.email ?? payload.email,
        });
        toast.success(JSON.stringify({ type: 'success', title: 'Code verified' }));
      } catch (err: any) {
        toast.error(JSON.stringify({ type: 'error', title: err?.response?.data?.message ?? 'Unable to verify code' }));
      } finally {
        setLoadingVerify(false);
      }
    },
  });

  const resetPasswordForm = useFormik({
    initialValues: {
      password: '',
      rPassword: '',
    },
    validationSchema: Yup.object({
      password: Yup.string()
        .min(8, 'Password must be more than 8 characters')
        .required('Password is required')
        .matches(/^(?=.*[a-z])/, 'Must Contain at least One Lowercase Character')
        .matches(/^(?=.*[A-Z])/, 'Must Contain at least One Uppercase Character')
        .matches(/^(?=.*[0-9])/, 'Must Contain at least One Number')
        .matches(/^(?=.*[!@#$%^&*\\|/{}()<>:;[\]_\\-\\=?])/, 'Must Contain at least One special case Character'),
      rPassword: Yup.string()
        .oneOf([Yup.ref('password'), null], 'Passwords do not match')
        .required('Please confirm your password'),
    }),
    onSubmit: async values => {
      setLoadingReset(true);
      try {
        const { data } = await api.post(
          '/auth/password/reset',
          { newPassword: values.password },
          {
            headers: {
              authorization: forgotStore?.token,
            },
          },
        );

        setAuth({
          user: data?.user ?? data?.resetUserPassword?.user,
          token: data?.token ?? data?.resetUserPassword?.token,
        });
        navigate({
          replace: true,
          to: search?.redirect ?? '/',
        });
      } catch (err: any) {
        toast.error(JSON.stringify({ type: 'error', title: err?.response?.data?.message ?? 'Unable to reset password' }));
      } finally {
        setLoadingReset(false);
      }
    },
  });

  return (
    <div className="mx-auto w-full max-w-sm lg:w-96">
      <div className="text-center">
        <h2 className="text-xl font-extrabold text-gray-900">
          {['send-code', 'verify-code'].includes((forgotStore as any).tab) ? 'Forgot your password?' : 'Update your password'}
        </h2>
        <p className="mt-2 text-sm text-gray-600">
          {forgotStore?.tab === 'send-code'
            ? 'Enter your email address to receive an email with a verification code.'
            : forgotStore?.tab === 'verify-code'
              ? `Enter the 6 digit code sent to your email address (${forgotStore?.email})`
              : `Hello ${forgotStore?.email}, kindly enter a new password to use with your account`}
        </p>
      </div>

      <div className="mt-8">
        <div className="mt-6">
          {forgotStore?.tab === 'send-code' && (
            <form onSubmit={sendCodeForm.handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  Email address
                </label>
                <div className="mt-1">
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    value={sendCodeForm.values.email}
                    onChange={sendCodeForm.handleChange}
                    placeholder="eg. user@wewire.com"
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  />
                </div>
                <p className="mt-2 text-sm text-red-600" id="email-error">
                  {sendCodeForm.touched.email && sendCodeForm.errors.email}
                </p>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center"></div>

                <div className="text-sm text-gray-600">
                  Remember your password?{' '}
                  <Link to="/login" className="font-medium text-primary-600 hover:text-primary-500">
                    Login
                  </Link>
                </div>
              </div>

              <div>
                <button
                  type="submit"
                  disabled={loadingSend}
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded shadow-sm text-sm font-medium text-white bg-gradient-to-r bg-primary-600  hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  {loadingSend ? 'Sending Reset Code...' : 'Send Reset Code'}
                </button>
              </div>
            </form>
          )}
          {forgotStore?.tab === 'verify-code' && (
            <form onSubmit={verifyCodeForm.handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="code" className="block text-sm font-medium text-gray-700">
                  Verification Code
                </label>
                <div className="mt-1">
                  <input
                    id="code"
                    name="code"
                    type="text"
                    value={verifyCodeForm.values.code}
                    onChange={verifyCodeForm.handleChange}
                    maxLength={6}
                    minLength={6}
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  />
                </div>
                <p className="mt-2 text-sm text-red-600" id="email-error">
                  {verifyCodeForm.touched.code && verifyCodeForm.errors.code}
                </p>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center"></div>

                <div className="text-sm text-gray-600">
                  Didn't receive code?{' '}
                  <button
                    type="button"
                    disabled={loadingSend}
                    onClick={() => sendCodeForm.handleSubmit()}
                    className="font-medium text-primary-600 hover:text-primary-500"
                  >
                    Resend
                  </button>
                </div>
              </div>

              <div>
                <button
                  type="submit"
                  disabled={loadingVerify}
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  {loadingVerify ? 'Verifying Reset Code...' : 'Verify Reset Code'}
                </button>
              </div>
            </form>
          )}
          {forgotStore?.tab === 'reset-password' && (
            <form onSubmit={resetPasswordForm.handleSubmit} className="space-y-6">
              <div className="space-y-1">
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  New Password
                </label>
                <div className="mt-1">
                  <input
                    id="password"
                    name="password"
                    type="password"
                    value={resetPasswordForm.values.password}
                    onChange={resetPasswordForm.handleChange}
                    onBlur={resetPasswordForm.handleBlur}
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  />
                </div>
                <div className="mt-2 text-xs text-gray-600 space-y-1">
                  <p className="font-medium">Password must contain:</p>
                  <ul className="list-disc list-inside space-y-0.5">
                    <li>At least 8 characters</li>
                    <li>One uppercase letter</li>
                    <li>One lowercase letter</li>
                    <li>One number</li>
                    <li>One special character (!@#$%^&*()-_=+{};:,&lt;.&gt;)</li>
                  </ul>
                </div>
                <p className="mt-2 text-sm text-red-600" id="email-error">
                  {resetPasswordForm.touched.password && resetPasswordForm.errors.password}
                </p>
              </div>

              <div className="space-y-1">
                <label htmlFor="rPassword" className="block text-sm font-medium text-gray-700">
                  Repeat Password
                </label>
                <div className="mt-1">
                  <input
                    id="rPassword"
                    name="rPassword"
                    type="password"
                    value={resetPasswordForm.values.rPassword}
                    onChange={resetPasswordForm.handleChange}
                    onBlur={resetPasswordForm.handleBlur}
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  />
                </div>
                <p className="mt-2 text-sm text-red-600" id="email-error">
                  {resetPasswordForm.touched.rPassword && resetPasswordForm.errors.rPassword}
                </p>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <input
                    id="remember_me"
                    name="remember_me"
                    type="checkbox"
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <label htmlFor="remember_me" className="ml-2 block text-sm text-gray-900">
                    Remember me
                  </label>
                </div>
              </div>

              <div>
                <button
                  type="submit"
                  disabled={loadingReset}
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  {loadingReset ? 'Reseting password...' : 'Reset password'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
